import { Brand } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import { TelemetryErrorBoundary as ErrorBoundary } from "@mixitone/telemetry";
import { platform } from "@tauri-apps/plugin-os";
import clsx from "clsx";
import { GlobalSearch } from "components/GlobalSearch";
import { useHeaderElement } from "components/hooks/useHeaderElement";
import tauri from "lib/tauri";
import React, { Suspense } from "react";
import OfflineNotification from "./OfflineNotification";
import TopNav from "./TopNav/TopNav";

const TauriCustomTitlebar = React.lazy(() => import("./TauriCustomTitlebar"));

const wwwUrl = import.meta.env.VITE_WWW;

/** Native mac uses overlay traffic lights; hide HTML duplicates from tauri-controls. */
function hideHtmlWindowControls(): boolean {
  if (!tauri.enabled) return false;
  try {
    return platform() === "macos";
  } catch {
    return false;
  }
}

function useCustomTitlebar(): boolean {
  if (!tauri.enabled) return false;
  try {
    return platform() !== "macos";
  } catch {
    return false;
  }
}

const Layout: React.FC<{
  menu?: React.ReactNode;
  children: React.ReactNode;
}> = ({ menu, children }) => {
  const ref = useHeaderElement();
  const hideControls = hideHtmlWindowControls();
  const customTitlebar = useCustomTitlebar();
  const showTauriBrand = tauri.enabled && !hideControls;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {customTitlebar ? (
        <div ref={ref} className="relative z-0 shrink-0">
          <Suspense fallback={null}>
            <TauriCustomTitlebar menu={menu} hideControls={hideControls} />
          </Suspense>
        </div>
      ) : (
        <div
          ref={ref}
          data-tauri-drag-region
          className={clsx(
            "relative z-0 flex w-full shrink-0 items-center border-b border-b-slate-900 bg-slate-800 py-2 transition-all select-none mixitone-header",
            hideControls ? "mixitone-macos-overlay-titlebar" : "pl-3",
          )}
        >
          <div className="flex min-w-0 flex-1 items-center">
            {(!tauri.enabled || showTauriBrand) && (
              <div data-tauri-drag-region className="flex min-h-10 shrink-0 items-center">
                {!tauri.enabled && (
                  <a href={wwwUrl}>
                    <Brand
                      color="dark"
                      className="w-[170px] text-left text-xl text-scale-1200 tall:@sm/app:w-[200px] tall:@sm/app:text-3xl"
                    />
                  </a>
                )}
                {showTauriBrand && (
                  <Brand
                    color="dark"
                    className="pointer-events-none w-[170px] text-left text-xl text-scale-1200 tall:@sm/app:w-[200px] tall:@sm/app:text-3xl"
                  />
                )}
              </div>
            )}
            <GlobalSearch />
          </div>
          <div className="flex shrink-0 items-center justify-end">
            {menu}
            <TopNav />
          </div>
        </div>
      )}
      {/* Pull up slightly so a rotated selected SquareTab can overlap the header instead of clipping.
          Avoid z-index here: it stacks this pane above the session drag overlay and breaks drops. */}
      <div className="relative -mt-2 flex min-h-0 flex-1 flex-col overflow-hidden pt-2">
        <ErrorBoundary fallback={<div className="p-5">Error</div>}>{children}</ErrorBoundary>
      </div>
      {tauri.enabled && <OfflineNotification />}
    </div>
  );
};

export default ApplicationView(Layout);
